import { CSSProperties, FC } from 'react'
import styled from 'styled-components'

import CrossIcon from '../icons/CrossOut'
import SmallCloseIcon from '../icons/AirbnbClose'
import CloseButton from './CloseButton'
import Modal, { ModalProps } from './Modal'
import { media } from '../../styles/media'
import typeStyle from '../../styles/typeStyle'

type DialogBodyProps = {
  noPadding?: boolean
  reducedBottomPadding?: boolean
  smallModal?: boolean
}

const DialogBody = styled.div<DialogBodyProps>`
  ${typeStyle('-ds-font-body-light')}
  position: relative;
  padding: 64px 16px;

  ${({ noPadding }) =>
    noPadding &&
    `
    padding: 0 !important;
  `}

  ${media.sm<DialogBodyProps>`
    padding: ${({ smallModal, reducedBottomPadding }) => {
      if (smallModal) return '40px 16px'
      if (reducedBottomPadding) return ' 64px 16px 30px'
      return '64px 16px 64px'
    }};
  `}
`

const Title = styled.h1<{
  titleAlign?: 'left' | 'right' | 'center'
}>`
  ${typeStyle('-ds-font-title-1')}
  margin: 0;
  margin-bottom: 40px;
  text-align: ${({ titleAlign }) => titleAlign || 'center'};
`

export type DialogModalProps = {
  title?: string
  titleAlign?: 'left' | 'right' | 'center'
  onRequestClose?: (...args: any) => any
  noPadding?: boolean
  reducedBottomPadding?: boolean
  fullScreen?: boolean
  closeButtonColour?: '-ds-neutral-700' | '-ds-neutral-100' | '-ds-core-400'
  style?: CSSProperties
  white?: boolean
  rounded?: boolean
  narrow?: boolean
  mobileCentered?: boolean
  smallModal?: boolean
  autoWidth?: boolean
  disableClose?: boolean
} & ModalProps

const DialogModal: FC<DialogModalProps> = ({
  title,
  titleAlign,
  onRequestClose,
  children,
  noPadding,
  reducedBottomPadding,
  closeButtonColour,
  fullScreen = false,
  white = false,
  rounded,
  narrow = false,
  mobileCentered = false,
  smallModal,
  autoWidth,
  disableClose,
  ...props
}) => {
  const modalClassName = [
    'ReactModal__Content',
    fullScreen ? 'c-fullscreen-modal__content' : 'c-basic-modal__content',
    white ? 'white' : '',
    rounded && !fullScreen ? 'rounded' : '',
    narrow && !fullScreen ? 'narrow' : '',
    autoWidth ? 'auto-width' : '',
    mobileCentered && 'mobile-centered',
  ].join(' ')

  const modalOverlayClassName = [
    'ReactModal__Overlay',
    fullScreen ? 'c-fullscreen-modal__overlay' : 'c-basic-modal__overlay',
  ].join(' ')

  return (
    <Modal
      shouldCloseOnOverlayClick={!disableClose}
      {...props}
      onRequestClose={onRequestClose}
      className={modalClassName}
      overlayClassName={modalOverlayClassName}
    >
      <DialogBody
        noPadding={noPadding}
        reducedBottomPadding={reducedBottomPadding}
        smallModal={smallModal}
      >
        {title && <Title titleAlign={titleAlign}>{title}</Title>}

        <div>{children}</div>

        {!disableClose && (
          <CloseButton
            aria-label="Close modal"
            onClick={onRequestClose}
            $colour={closeButtonColour || '-ds-core-400'}
            $small={smallModal}
          >
            {smallModal ? <SmallCloseIcon /> : <CrossIcon />}
          </CloseButton>
        )}
      </DialogBody>
    </Modal>
  )
}

export default DialogModal
